import TimerSurfaceService from '../../services/TimerSurfaceService';
import { recipeTimerRegistry } from './recipeStepTimers';

const opaqueOwner = value => {
  let hash = 2166136261;
  for (const char of String(value)) { hash ^= char.charCodeAt(0); hash = Math.imul(hash, 16777619); }
  return (hash >>> 0).toString(36);
};

// Clerk identity is only an ownership marker; timer storage remains scoped by
// backend user ID. No raw identity is used as a native timer ID or storage key.
export async function reconcileRecipeTimerIdentity(ownerId, {
  storage = null,
  stop = TimerSurfaceService.stopRecipeTimers,
} = {}) {
  try {
    storage = storage || window.localStorage;
    const previousOwner = storage.getItem('recipe-timers-auth-owner');
    const scopeId = storage.getItem('recipe-timers-active-scope');
    if (scopeId && (!ownerId || (previousOwner && previousOwner !== opaqueOwner(ownerId)))) {
      const result = await stop({ scopeId });
      if (result?.ok === false && result.reason !== 'not-native' && result.reason !== 'not-supported') return result;
      storage.removeItem('recipe-timers-active-scope');
    }
    if (ownerId) storage.setItem('recipe-timers-auth-owner', opaqueOwner(ownerId));
    else storage.removeItem('recipe-timers-auth-owner');
    return { ok: true };
  } catch (_) { return { ok: false, reason: 'storage-unavailable' }; }
}

// One writer for native snapshots. Revoke synchronously at an auth change,
// then serialize the old-scope stop and the new-scope publish.
export function createRecipeTimerSession({ storage, stop = TimerSurfaceService.stopRecipeTimers, registry = recipeTimerRegistry } = {}) {
  let activeUser = null;
  let alarmStatus = null;
  let generation = 0;
  let queue = Promise.resolve();
  const canPublish = userId => !!activeUser && String(activeUser) === String(userId);
  registry.setPublishGuard(canPublish);
  return {
    canPublish,
    getAlarmStatus(userId) { return canPublish(userId) ? alarmStatus : null; },
    transition(ownerId, userId) {
      const version = ++generation;
      activeUser = null;
      alarmStatus = null;
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('recipe-timer-authorization-changed'));
      const task = async () => {
        const result = await reconcileRecipeTimerIdentity(ownerId, { storage, stop });
        if (!result.ok || version !== generation) return result;
        if (ownerId && userId) {
          const synced = await registry.sync(userId, { authorized: true });
          if (synced?.ok === false && synced.reason !== 'not-native' && synced.reason !== 'not-supported') return synced;
          if (version !== generation) return { ok: true };
          activeUser = userId;
          alarmStatus = synced;
          if (typeof window !== 'undefined') window.dispatchEvent(new Event('recipe-timer-authorization-changed'));
        }
        return { ok: true };
      };
      queue = queue.then(task, task);
      return queue;
    },
  };
}

export const recipeTimerSession = createRecipeTimerSession();
